// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import UpsertEquipmentForm from 'OnEquip/equipment/common/upsert-equipment-form';
import {fetchEffectData, useLazyRef} from 'Utils/react-utils';
import {createEquipment} from 'Services/equipment-service';
import {getFleetEquipmentById, getModels} from 'Services/fleet-service';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {formatModels} from 'Utils/multiselect/service-multiselect-mappers';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';

function EquipmentCreateDialog(props) {
    const {
        closeDialog,
        fleetId,
        invalidInputs,
        setValid,
        translations,
        updateFleetEquipment
    } = props;

    const modelMap = useLazyRef(() => new Map());

    const [equipmentName, setEquipmentName] = React.useState(() => '');
    const [isSaving, setIsSaving] = React.useState(() => false);
    const [loading, setLoading] = React.useState(() => true);
    const [modelItems, setModelItems] = React.useState(() => []);
    const [selectedModel, setSelectedModel] = React.useState();
    const [serialNumber, setSerialNumber] = React.useState(() => '');

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);
        const [{models}, {equipment}] = await Promise.all([
            getModels(),
            getFleetEquipmentById(fleetId)
        ]);

        if (isMounted()) {
            const {fullListModels} = formatModels(models, equipment, true);

            fullListModels.forEach((model) => {
                modelMap.current.set(model.id, model);
            });

            setModelItems(fullListModels);
        }
        setLoading(false);
    }), [fleetId]);

    async function onSave() {
        const equipmentData = {
            equipmentName,
            fleetId,
            model: modelMap.current.get(selectedModel),
            serialNumber
        };

        equipmentData.modelId = equipmentData.model.modelId;

        setIsSaving(true);

        try {
            await createEquipment(equipmentData);
        } catch (e) {
            setIsSaving(false);

            throw e;
        }

        updateFleetEquipment();
        closeDialog();
    }

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className={'equipment-create-dialog dropdown-overflow-dialog'}
            closeHandler={closeDialog}
            disableSave={invalidInputs.size > 0 || isSaving}
            footerLoading={isSaving}
            onSave={onSave}
            saveLabel={translations.save}
            title={translations.equipment_add_equipment}
            translations={translations}
        >
            <LoadingWrapper
                className='table-loading-icon'
                loading={loading}
                size='50px'
            >
                <UpsertEquipmentForm
                    equipmentName={equipmentName}
                    modelItems={modelItems}
                    selectedModel={selectedModel}
                    serialNumber={serialNumber}
                    setEquipmentName={setEquipmentName}
                    setSelectedModel={setSelectedModel}
                    setSerialNumber={setSerialNumber}
                    setValid={setValid}
                    shouldDisplayModelSelector={true}
                    translations={translations}
                />
            </LoadingWrapper>
        </FormDialog>
    );
}

EquipmentCreateDialog.propTypes = {
    closeDialog: PropTypes.func,
    fleetId: PropTypes.string,
    invalidInputs: PropTypes.instanceOf(Set),
    setValid: PropTypes.func,
    translations: PropTypes.translations,
    updateFleetEquipment: PropTypes.func
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.EQUIPMENT_CREATE_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(EquipmentCreateDialog));
